/**
 * Нижняя шторка новой темы: .sheet.sheet--bottom + .backdrop,
 * разметка из прототипа (.sheet__head · .sheet__title · .sheet__close · .sheet__body).
 */
import React from 'react'
import { createPortal } from '@/utils/portal'
import { isBrowser } from '@/utils/env'
import { useAppContext } from '@/pages/_app'

export type ModernSheetProps = {
  title: string
  isOpen: boolean
  onClose: () => void
  tall?: boolean
  /** снимок во весь верх шторки вместо полосы заголовка */
  hero?: boolean
  children: React.ReactNode
  /** закреплённый низ шторки — .sheet__foot из прототипа */
  foot?: React.ReactNode
  /** перекрытие поверх шторки (выбор способа оплаты) — .co-pay из прототипа */
  overlay?: React.ReactNode
  /** вложенная шторка (подтверждение поверх оформления): не закрывает ту,
      из которой открыта */
  stacked?: boolean
}

/**
 * Слой шторок — отдельный узел в конце документа. Он несёт на себе
 * data-theme, иначе правила темы к вынесенному содержимому не применяются:
 * все они начинаются с [data-theme='modern'].
 */
function getSheetLayer(): Element {
  const existing = document.getElementById(SHEET_LAYER_ID)
  if (existing) {
    // после ухода в панель управления слой переводится на прежнее
    // оформление (MenuThemeSync) — шторка новой темы возвращает своё
    if (existing.getAttribute('data-theme') !== 'modern') {
      existing.setAttribute('data-theme', 'modern')
    }
    syncDarkClass(existing)
    return existing
  }
  const layer = document.createElement('div')
  layer.setAttribute('id', SHEET_LAYER_ID)
  layer.setAttribute('data-theme', 'modern')
  syncDarkClass(layer)
  document.body.appendChild(layer)
  return layer
}

/**
 * Тёмную палитру включает класс dark на том же узле, что и тема. Слой шторок
 * живёт отдельным узлом в конце страницы, вне оболочки приложения, поэтому
 * класс нужно перенести: без него у заведения с тёмным меню шторки выходили
 * белыми на чёрной странице.
 */
function syncDarkClass(layer: Element) {
  const source = document.querySelector(`[data-theme='modern'].dark`)
  layer.classList.toggle('dark', !!source)
}

const SHEET_LAYER_ID = 'modern-sheet-layer'

/**
 * Открытые шторки — стопкой, последняя сверху. Одновременно открыта одна:
 * новая закрывает предыдущие (раньше карточка блюда ложилась поверх формы
 * брони, и форма была недоступна). Исключение — вложенная (stacked).
 * Escape закрывает верхнюю. Блокировка прокрутки страницы держится, пока
 * в стопке есть хоть одна шторка.
 */
const открытые: { id: symbol; закрыть: () => void }[] = []

function обновитьБлокировку() {
  // ширина полосы прокрутки — до блокировки, пока полоса ещё на месте:
  // на время шторки её заменяет отступ той же ширины (body.sheet-open)
  if (открытые.length > 0 && !document.body.classList.contains('sheet-open')) {
    const полоса = window.innerWidth - document.documentElement.clientWidth
    document.body.style.setProperty('--sheet-gutter', `${полоса}px`)
  }
  document.body.classList.toggle('sheet-open', открытые.length > 0)
}

export function ModernSheet(props: ModernSheetProps) {
  const { t } = useAppContext()
  const id = React.useRef(Symbol('sheet'))
  const закрыть = React.useRef(props.onClose)
  // eslint-disable-next-line immutable/no-mutation
  закрыть.current = props.onClose

  // На сервере шторки нет, поэтому первый клиентский рендер тоже обязан быть
  // пустым — иначе разметка не совпадёт с серверной и React ругается на
  // гидратацию. Портал создаём вторым рендером, после монтирования.
  const [isMounted, setIsMounted] = React.useState(false)
  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  // Пока шторка открыта, страница под ней не должна прокручиваться: иначе
  // прокрутка внутри шторки «протекает» на меню и оно уезжает под ней.
  React.useEffect(() => {
    if (!props.isOpen) {
      return
    }
    const свой = id.current
    if (!props.stacked) {
      открытые
        .filter((шторка) => {
          return шторка.id !== свой
        })
        .forEach((шторка) => {
          шторка.закрыть()
        })
    }
    открытые.push({
      id: свой,
      закрыть: () => {
        закрыть.current()
      },
    })
    обновитьБлокировку()
    const поКлавише = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && открытые[открытые.length - 1]?.id === свой) {
        закрыть.current()
      }
    }
    document.addEventListener('keydown', поКлавише)
    return () => {
      document.removeEventListener('keydown', поКлавише)
      const место = открытые.findIndex((шторка) => {
        return шторка.id === свой
      })
      if (место >= 0) {
        открытые.splice(место, 1)
      }
      обновитьБлокировку()
    }
  }, [props.isOpen, props.stacked])

  // Шторку и затемнение выносим в конец документа. Иначе они остаются
  // внутри своего родителя (например, колонки корзины) и оказываются
  // заперты в его слое: панель разделов и инструменты снимка светились
  // поверх затемнения, сколько бы z-index мы шторке ни задали.
  const sheet = (
    <>
      <div
        className="backdrop"
        data-open={String(props.isOpen)}
        onClick={props.onClose}
        aria-hidden="true"
      />
      <div
        className={`sheet sheet--bottom${props.tall ? ' sheet--tall' : ''}${
          props.hero ? ' sheet--hero' : ''
        }`}
        data-open={String(props.isOpen)}
        aria-hidden={!props.isOpen}
        aria-label={props.title}
      >
        {props.hero ? (
          // заголовок живёт на снимке, поэтому полосы над шторкой нет —
          // остаётся только кнопка закрытия, плавающая поверх кадра
          <button
            type="button"
            className="sheet__close sheet__close--float"
            aria-label={t('common:close')}
            onClick={props.onClose}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        ) : (
          <div className="sheet__head">
            <h2 className="sheet__title">{props.title}</h2>
            <button
              type="button"
              className="sheet__close"
              aria-label={t('common:close')}
              onClick={props.onClose}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        )}
        <div className="sheet__body">{props.children}</div>
        {props.foot}
        {props.overlay}
      </div>
    </>
  )

  if (!isBrowser || !isMounted) {
    return null
  }

  return createPortal(sheet, getSheetLayer())
}
